import { supabase } from '../lib/supabase';

interface SpeedingEventData {
  truckId: string;
  samsaraVehicleId: string;
  unitNumber: string;
  speedMph: number;
  speedLimitMph: number;
  latitude: number;
  longitude: number;
  eventTime: string;
  driverId?: string;
  driverName?: string;
}

/**
 * Log a speeding event to the database
 * This function checks if the event already exists to avoid duplicates
 */
export async function logSpeedingEvent(eventData: SpeedingEventData): Promise<{
  success: boolean;
  error?: string;
}> {
  try {
    const speedOverLimit = eventData.speedMph - eventData.speedLimitMph;
    
    // Determine severity based on how much over the limit
    let severity = 'low';
    if (speedOverLimit >= 20) {
      severity = 'critical';
    } else if (speedOverLimit >= 15) {
      severity = 'high';
    } else if (speedOverLimit >= 10) {
      severity = 'medium';
    }

    // Check if this event already exists (within 5 minutes)
    const fiveMinutesAgo = new Date(new Date(eventData.eventTime).getTime() - 5 * 60 * 1000).toISOString();
    const { data: existingEvents, error: checkError } = await supabase
      .from('samsara_safety_events')
      .select('id')
      .eq('samsara_vehicle_id', eventData.samsaraVehicleId)
      .eq('event_type', 'speeding')
      .gte('event_time', fiveMinutesAgo)
      .lte('event_time', eventData.eventTime);

    if (checkError) {
      console.error('Error checking existing events:', checkError);
      return { success: false, error: checkError.message };
    }

    // If event already exists, don't create duplicate
    if (existingEvents && existingEvents.length > 0) {
      return { success: true }; // Silent success, event already logged
    }

    // Insert the new speeding event
    const { error: insertError } = await supabase
      .from('samsara_safety_events')
      .insert({
        truck_id: eventData.truckId,
        samsara_vehicle_id: eventData.samsaraVehicleId,
        event_type: 'speeding',
        event_time: eventData.eventTime,
        severity,
        driver_id: eventData.driverId || null,
        driver_name: eventData.driverName || null,
        latitude: eventData.latitude,
        longitude: eventData.longitude,
        speed_mph: eventData.speedMph,
        speed_limit_mph: eventData.speedLimitMph,
        speed_over_limit_mph: speedOverLimit,
        raw_event_data: {
          source: 'live_map',
          unit_number: eventData.unitNumber,
          detected_at: new Date().toISOString()
        }
      });

    if (insertError) {
      console.error('Error inserting speeding event:', insertError);
      return { success: false, error: insertError.message };
    }

    console.log(`✅ Logged speeding event: ${eventData.unitNumber} - ${eventData.speedMph} mph (${speedOverLimit} over limit)`);
    return { success: true };
  } catch (error: any) {
    console.error('Error logging speeding event:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Get the current driver assigned to a truck based on active dispatches
 */
export async function getCurrentDriverForTruck(truckId: string): Promise<{
  driverId?: string;
  driverName?: string;
}> {
  try {
    // Get active dispatches for this truck
    const { data: dispatches, error } = await supabase
      .from('dispatches')
      .select(`
        driver_id,
        load_id,
        drivers!dispatches_driver_id_fkey (
          id,
          first_name,
          last_name
        )
      `)
      .eq('truck_id', truckId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching driver for truck:', error);
      return {};
    }

    if (dispatches && dispatches.length > 0) {
      // Check each dispatch to find one with an active load
      for (const dispatch of dispatches) {
        // Get the load status
        const { data: load } = await supabase
          .from('loads')
          .select('status')
          .eq('id', dispatch.load_id)
          .single();

        if (load && ['BOOKED', 'DISPATCHED', 'LOADED', 'booked', 'dispatched', 'loaded'].includes(load.status)) {
          const driver = dispatch.drivers as any;
          
          if (driver) {
            return {
              driverId: driver.id,
              driverName: `${driver.first_name} ${driver.last_name}`
            };
          }
        }
      }
    }

    return {};
  } catch (error) {
    console.error('Error getting current driver:', error);
    return {};
  }
}
